import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Search, Filter, ChevronLeft, ChevronRight, Wallet, 
  CheckCircle, XCircle, Clock, AlertCircle, Banknote,
  Building2, CreditCard, User
} from 'lucide-react';
import { getWithdrawals, approveWithdrawal, rejectWithdrawal } from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ITEMS_PER_PAGE = 10;

const STATUS_CONFIG = {
  pending: { 
    label: 'Pending', 
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
    icon: Clock
  },
  completed: { 
    label: 'Approved', 
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
    icon: CheckCircle
  },
  rejected: { 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
    icon: XCircle
  },
};

export default function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const response = await getWithdrawals();
      setWithdrawals(response.data);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  // Count pending withdrawals
  const pendingCount = useMemo(() => {
    return withdrawals.filter(w => w.status === 'pending').length;
  }, [withdrawals]);

  // Filter and search
  const filteredWithdrawals = useMemo(() => {
    return withdrawals.filter(withdrawal => {
      const matchesSearch = searchQuery === '' || 
        withdrawal.teacherName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        withdrawal.teacherEmail?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || withdrawal.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [withdrawals, searchQuery, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredWithdrawals.length / ITEMS_PER_PAGE);
  const paginatedWithdrawals = filteredWithdrawals.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const handleApprove = async (withdrawalId) => {
    setActionLoading(withdrawalId);
    try {
      await approveWithdrawal(withdrawalId);
      setWithdrawals(prev => 
        prev.map(w => w.id === withdrawalId ? { ...w, status: 'completed' } : w)
      );
      if (selectedWithdrawal?.id === withdrawalId) {
        setSelectedWithdrawal(prev => ({ ...prev, status: 'completed' }));
      }
      toast.success('Withdrawal approved successfully');
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;
    
    setActionLoading(selectedWithdrawal.id);
    try {
      await rejectWithdrawal(selectedWithdrawal.id, rejectReason);
      setWithdrawals(prev => 
        prev.map(w => w.id === selectedWithdrawal.id ? { ...w, status: 'rejected' } : w)
      );
      setSelectedWithdrawal(prev => ({ ...prev, status: 'rejected' }));
      setRejectDialogOpen(false);
      setRejectReason('');
      toast.success('Withdrawal rejected');
    } catch (error) {
      toast.error('Failed to reject withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div data-testid="withdrawals-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Withdrawals
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Review and approve teacher withdrawal requests
          </p>
        </div>
        <div className="flex items-center gap-4">
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {pendingCount} pending approval
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-slate-400" />
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {filteredWithdrawals.length} requests
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="withdrawal-search-input"
                placeholder="Search by teacher name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="withdrawal-status-filter" className="w-full sm:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                <TableHead className="font-semibold">Teacher</TableHead>
                <TableHead className="font-semibold">Amount</TableHead>
                <TableHead className="font-semibold">Bank Details</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedWithdrawals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-slate-500">
                    No withdrawal requests found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedWithdrawals.map((withdrawal) => {
                  const statusConfig = STATUS_CONFIG[withdrawal.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow 
                      key={withdrawal.id} 
                      data-testid={`withdrawal-row-${withdrawal.id}`}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${withdrawal.status === 'pending' ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-medium">
                            {withdrawal.teacherName?.split(' ').slice(1).map(n => n[0]).join('') || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 dark:text-white">
                              {withdrawal.teacherName}
                            </div>
                            <div className="text-xs text-slate-500">{withdrawal.teacherEmail}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 font-semibold text-emerald-600 dark:text-emerald-400">
                          <Banknote className="w-4 h-4" />
                          R{withdrawal.amount?.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="text-slate-900 dark:text-white">
                            {withdrawal.bankDetails?.bankName || 'N/A'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {withdrawal.bankDetails?.accountNumber ? `****${withdrawal.bankDetails.accountNumber.slice(-4)}` : 'No account'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400">
                        {withdrawal.createdAt 
                          ? format(new Date(withdrawal.createdAt), 'MMM d, yyyy HH:mm')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {withdrawal.status === 'pending' && (
                            <>
                              <Button
                                data-testid={`withdrawal-approve-${withdrawal.id}`}
                                size="sm"
                                onClick={() => handleApprove(withdrawal.id)}
                                disabled={actionLoading === withdrawal.id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                data-testid={`withdrawal-reject-${withdrawal.id}`}
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal);
                                  setRejectDialogOpen(true);
                                }}
                                disabled={actionLoading === withdrawal.id}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedWithdrawal(withdrawal)}
                          >
                            View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredWithdrawals.length)} of {filteredWithdrawals.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="withdrawals-prev-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400 px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                data-testid="withdrawals-next-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Withdrawal Detail Modal */}
      <Dialog open={!!selectedWithdrawal && !rejectDialogOpen} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              Withdrawal Request
              {selectedWithdrawal && (
                <Badge className={STATUS_CONFIG[selectedWithdrawal.status]?.color}>
                  {STATUS_CONFIG[selectedWithdrawal.status]?.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Review withdrawal request details
            </DialogDescription>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="space-y-6">
              {/* Amount */}
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Withdrawal Amount</p>
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                  R{selectedWithdrawal.amount?.toFixed(2)}
                </p>
              </div>

              {/* Teacher Info */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Teacher Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Name</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedWithdrawal.teacherName}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedWithdrawal.teacherEmail}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Phone</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedWithdrawal.teacherPhone || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Bank Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Bank Name</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedWithdrawal.bankDetails?.bankName || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Account Type</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedWithdrawal.bankDetails?.accountType || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Account Number</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedWithdrawal.bankDetails?.accountNumber || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Branch Code</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedWithdrawal.bankDetails?.branchCode || 'Not provided'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Account Holder</span>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedWithdrawal.bankDetails?.accountHolder || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Request Date */}
              <div className="text-sm text-slate-500">
                Requested on: {selectedWithdrawal.createdAt 
                  ? format(new Date(selectedWithdrawal.createdAt), 'MMMM d, yyyy HH:mm')
                  : 'Unknown'
                }
              </div>

              {/* Actions */}
              {selectedWithdrawal.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    data-testid="withdrawal-modal-approve"
                    onClick={() => handleApprove(selectedWithdrawal.id)}
                    disabled={actionLoading === selectedWithdrawal.id}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Withdrawal
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={actionLoading === selectedWithdrawal.id}
                    className="flex-1"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedWithdrawal.status !== 'pending' && (
                <Button
                  variant="outline"
                  onClick={() => setSelectedWithdrawal(null)}
                  className="w-full"
                >
                  Close
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-600">Reject Withdrawal</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this withdrawal request. The teacher will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter rejection reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="min-h-24"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
